import { useEffect, useState } from "react";
import { api, API, logAudit } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Lock, FileText, Loader2, ShieldAlert } from "lucide-react";

// Secure, no-download document viewer with watermark + DLP protections.
export default function SecureDocViewer({ doc, open, onOpenChange, viewerName }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let url;
    if (open && doc && doc.storage_path) {
      setLoading(true);
      api.get(`/documents/${doc.id}/view`, { responseType: "blob" })
        .then((res) => { url = URL.createObjectURL(res.data); setBlobUrl(url); })
        .catch(() => setBlobUrl(null))
        .finally(() => setLoading(false));
    } else {
      setBlobUrl(null);
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [open, doc]);

  useEffect(() => {
    if (open && doc) logAudit("open_document_viewer", "documents", doc.id, { title: doc.title });
  }, [open, doc]);

  if (!doc) return null;
  const isPdf = doc.content_type === "application/pdf" || doc.file_ext === "pdf";
  const isImg = (doc.content_type || "").startsWith("image/");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] p-0 gap-0 flex flex-col" data-testid="secure-doc-viewer">
        <DialogHeader className="px-5 py-3 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="h-4 w-4 text-primary" /> {doc.title}
          </DialogTitle>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1">
            <Lock className="h-3 w-3" /> Confidential — view only. Download, print &amp; copy are disabled and logged.
          </div>
          <DialogDescription className="sr-only">Secure, view-only document viewer. Downloading, printing and copying are disabled for DLP compliance.</DialogDescription>
        </DialogHeader>

        <div className="relative flex-1 overflow-hidden bg-muted/30">
          <div className="dlp-watermark" />
          <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
            <span className="font-display font-extrabold text-primary/10 text-5xl rotate-[-25deg] select-none text-center leading-tight">
              CONFIDENTIAL<br />{viewerName || "MONGE & ASSOCIATES"}
            </span>
          </div>

          <div className="dlp-protected relative z-10 h-full overflow-y-auto p-6">
            {loading && (
              <div className="h-full grid place-items-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
            {!loading && doc.storage_path && isPdf && blobUrl && (
              <iframe title={doc.title} src={`${blobUrl}#toolbar=0&navpanes=0`} className="w-full h-full min-h-[60vh] border-0 bg-white" />
            )}
            {!loading && doc.storage_path && isImg && blobUrl && (
              <img src={blobUrl} alt={doc.title} className="max-w-full mx-auto pointer-events-none select-none" />
            )}
            {!loading && doc.storage_path && !isPdf && !isImg && (
              <div className="h-full grid place-items-center text-center text-muted-foreground gap-2">
                <ShieldAlert className="h-8 w-8" />
                <p className="text-sm max-w-sm">This file type ({doc.file_ext}) can only be viewed within its native app. Preview is restricted for DLP compliance.</p>
              </div>
            )}
            {!loading && !doc.storage_path && (
              <article className="max-w-2xl mx-auto bg-card border border-border p-8 shadow-sm">
                {doc.description && <p className="text-sm text-muted-foreground mb-4 italic">{doc.description}</p>}
                <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-foreground">{doc.body}</pre>
              </article>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
